import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { HookFormContext } from 'components/cef_form/hook-form/providers/HookFormProvider';
import InnerFormSection from 'components/ui-components/forms/InnerFormSection';
import HookFormSelect from 'components/cef_form/hook-form/HookFormSelect';
import HookFormTextInput from 'components/cef_form/hook-form/HookFormTextInput';
import HookFormDobInput from 'components/cef_form/hook-form/HookFormDobInput';
import CheckBox from 'components/ui-components/CheckBox';
import { formTitles } from 'constants/RegistrationForm/constants';
import { EMAIL_REGEX } from 'constants/constants';
import validateSelectDropdown from 'utils/hook-form/validateSelectDropdown';
import AdditionalContacts from '../AdditionalContacts';

const titlePlaceholder = 'Select';
const titleError = 'Please select a title';

const additionalContactFields = [
  {
    label: 'Name',
    id: 'name',
    type: 'text',
    requiredErrorMessage: 'Please enter your name',
  },
  {
    label: 'Email',
    id: 'email',
    type: 'email',
    requiredErrorMessage: 'Please enter your email address',
    pattern: {
      value: EMAIL_REGEX,
      message: 'Please enter a valid email address.',
    },
  },
  {
    label: 'Phone',
    id: 'telephone',
    type: 'tel',
    requiredErrorMessage: 'Please enter a valid phone number',
  },
];

const ContactDetails = ({ soleTrader }) => {
  const {
    watchedFields: {
      customer: { title, forename, surname, email, telephone },
      accountContactName,
      accountContactEmail,
      accountContactPhone,
      purchasingContactName,
      purchasingContactEmail,
      purchasingContactPhone,
    },
    setValue,
  } = useContext(HookFormContext);

  const contactDetailInputs = [
    {
      id: 'customer.forename',
      label: 'First Name',
      error: 'Please enter your first name',
      type: 'text',
      value: forename,
    },
    {
      id: 'customer.surname',
      label: 'Last Name',
      error: 'Please enter your last name',
      type: 'text',
      value: surname,
    },
    {
      id: 'customer.email',
      label: 'Email',
      error: 'Please enter your email address',
      type: 'email',
      value: email,
      pattern: {
        value: EMAIL_REGEX,
        message: 'Please enter a valid email address.',
      },
    },
    {
      id: 'customer.telephone',
      label: 'Phone',
      error: 'Please enter a valid phone number',
      type: 'tel',
      value: telephone,
    },
  ];

  const [additionalAccountsContact, setAdditionalAccountsContact] = useState(
    !!accountContactName,
  );
  const [additionalPurchaseContact, setAdditionalPurchaseContact] = useState(
    !!purchasingContactName,
  );

  const additionalAccountsContactValues = {
    name: accountContactName,
    email: accountContactEmail,
    telephone: accountContactPhone,
  };

  const additionalPurchaseContactValues = {
    name: purchasingContactName,
    email: purchasingContactEmail,
    telephone: purchasingContactPhone,
  };

  useEffect(() => {
    if (!additionalAccountsContact) {
      setValue('account_contact_name', '');
      setValue('account_contact_email', '');
      setValue('account_contact_telephone', '');
    }

    if (!additionalPurchaseContact) {
      setValue('purchasing_contact_name', '');
      setValue('purchasing_contact_email', '');
      setValue('purchasing_contact_telephone', '');
    }
  }, [additionalAccountsContact, additionalPurchaseContact]);

  return (
    <>
      <InnerFormSection title="Your Details">
        <HookFormSelect
          id="customer.title"
          label="Title"
          value={title}
          selectItems={formTitles}
          rules={{
            required: titleError,
            validate: validateSelectDropdown(titlePlaceholder, titleError),
          }}
          className="mt-20 width-40"
          placeholder={titlePlaceholder}
        />

        {contactDetailInputs.map(
          ({ id, label, error, pattern, description, type, value }) => (
            <HookFormTextInput
              key={id}
              className="mt-20"
              type={type}
              id={id}
              label={label}
              description={description}
              value={value}
              rules={{
                required: error,
                pattern: {
                  ...pattern,
                },
              }}
            />
          ),
        )}

        {/* Show Dob inputs if businessType = Sole Trader */}
        {soleTrader && <HookFormDobInput />}
      </InnerFormSection>

      {/* Show Additional contatcs if any business but Sole Trader is selected */}
      {!soleTrader && (
        <InnerFormSection title="Additional Contacts <span>(Optional)</span>">
          <p className="fs-16 fw-400 ">
            Please select the contact you would like to add
          </p>

          <CheckBox
            id="additionalAccountsContact"
            value={additionalAccountsContact}
            onChange={() => setAdditionalAccountsContact(curState => !curState)}
            label="Add Accounts Contact"
            className="fl mt-14 mb-3"
            labelClass="fw-600 fs-16"
          />

          {additionalAccountsContact && (
            <AdditionalContacts
              /**
               * id's are needed for name, email and telephone
               * to save writing them all out I've added the main name here
               * inside this component I append the specific name to form account_contact_name, etc
               */
              id="account_contact"
              required={additionalAccountsContact}
              inputs={additionalContactFields}
              values={additionalAccountsContactValues}
            />
          )}

          <CheckBox
            id="additionalPurchasingContact"
            value={additionalPurchaseContact}
            onChange={() => setAdditionalPurchaseContact(curState => !curState)}
            label="Add Purchasing Contact"
            className="fl mt-14 mb-3"
            labelClass="fw-600 fs-16"
          />

          {additionalPurchaseContact && (
            <AdditionalContacts
              id="purchasing_contact"
              required={additionalPurchaseContact}
              inputs={additionalContactFields}
              values={additionalPurchaseContactValues}
            />
          )}
        </InnerFormSection>
      )}
    </>
  );
};

ContactDetails.propTypes = {
  soleTrader: PropTypes.bool,
};

export default ContactDetails;
